// =============================================================================
// CARD SECTION COMPONENT (V2 - NORMALIZED)
// =============================================================================

import React from 'react';
import { Card } from '@/components/Card';
import { Button } from '@/components/Button';
import { createActionHandler, convertButtonToAction } from '@/utils/actionHandler';
import { layoutToClasses, displayToStyles } from '@/types/layout';
import type { ContentComponent } from '@/types';
import type { CardComponent } from '@/types/components';

interface CardSectionProps {
  component: ContentComponent;
}

/**
 * Type guard for new CardComponent format
 */
function isNewCardFormat(component: ContentComponent): boolean {
  return (
    component.type === 'card' &&
    'items' in component &&
    Array.isArray((component as any).items)
  );
}

/**
 * Card Section Component
 * Supports both old and new formats
 */
export const CardSection: React.FC<CardSectionProps> = ({ component }) => {
  if (isNewCardFormat(component)) {
    return <CardSectionNew component={component as unknown as CardComponent} />;
  }
  return <CardSectionLegacy component={component} />;
};

/**
 * New format Card Section
 */
const CardSectionNew: React.FC<{ component: CardComponent }> = ({ component }) => {
  const { id, content, items, layout, display } = component;
  
  const layoutClasses = layoutToClasses(layout);
  const displayStyles = displayToStyles(display);
  
  return (
    <section id={id} className={`content-section card-section ${layoutClasses}`} style={displayStyles}>
      <div className="container">
        {content.title && (
          <div className="section-header">
            <h2>{content.title}</h2>
            {content.description && <p>{content.description}</p>}
          </div>
        )}
        
        <div className={`grid grid-cols-${layout.columns || 3}`}>
          {items.map((item, index) => (
            <Card
              key={item.id || index}
              title={item.title}
              subtitle={item.subtitle}
              image={item.image}
              actions={
                item.actions && item.actions.length > 0 ? (
                  <div className="card-actions">
                    {item.actions.map((action, actionIndex) => (
                      <Button
                        key={action.id || actionIndex}
                        variant={action.variant || 'primary'}
                        size={action.size || 'md'}
                        onClick={createActionHandler(action)}
                        disabled={action.disabled}
                      >
                        {action.label}
                      </Button>
                    ))}
                  </div>
                ) : undefined
              }
            >
              {item.body && (
                <div dangerouslySetInnerHTML={{ __html: item.body }} />
              )}
              
              {/* Portfolio-specific metadata */}
              {item.metadata && (
                <div className="portfolio-card-metadata">
                  {Array.isArray(item.metadata.technologies) && item.metadata.technologies.length > 0 && (
                    <div className="project-technologies">
                      {(item.metadata.technologies as string[]).map((tech: string, techIndex: number) => (
                        <span key={techIndex} className="tech-tag">{tech}</span>
                      ))}
                    </div>
                  )}
                  
                  {Array.isArray(item.metadata.features) && item.metadata.features.length > 0 && (
                    <div className="project-features">
                      <ul>
                        {(item.metadata.features as string[]).map((feature: string, featureIndex: number) => (
                          <li key={featureIndex}>{feature}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  {item.metadata.status ? (
                    <div className="project-status">
                      {String(item.metadata.status)}
                    </div>
                  ) : null}
                  
                  {item.metadata.launchDate ? (
                    <div className="project-launch-date">
                      <strong>Launched:</strong> {String(item.metadata.launchDate)}
                    </div>
                  ) : null}
                </div>
              )}
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};

/**
 * Legacy format Card Section
 */
const CardSectionLegacy: React.FC<{ component: ContentComponent }> = ({ component }) => {
  const { id, title, content, metadata } = component;
  
  const cards = metadata?.cards || [];
  const columns = metadata?.columns || 3;
  
  return (
    <section id={id} className="content-section card-section">
      <div className="container">
        {title && (
          <div className="section-header">
            <h2>{title}</h2>
            {content && <p>{content}</p>}
          </div>
        )}
        
        <div className={`grid grid-cols-${columns}`}>
          {cards.map((card: any, index: number) => {
            // Convert old button format to actions
            const actions = card.button 
              ? [convertButtonToAction(card.button)]
              : card.buttons?.map(convertButtonToAction) || [];
            
            return (
              <Card
                key={index}
                title={card.title}
                subtitle={card.subtitle}
                image={card.image}
                actions={
                  actions.length > 0 ? (
                    <div className="card-actions">
                      {actions.map((action: any, actionIndex: number) => (
                        <Button
                          key={actionIndex}
                          variant={action.variant || 'primary'}
                          onClick={createActionHandler(action)}
                        >
                          {action.label}
                        </Button>
                      ))}
                    </div>
                  ) : undefined
                }
              >
                {card.content && (
                  <div dangerouslySetInnerHTML={{ __html: card.content }} />
                )}
                
                {card.metadata && (
                  <div className="portfolio-card-metadata">
                    {card.metadata.technologies && (
                      <div className="project-technologies">
                        {card.metadata.technologies.map((tech: string, techIndex: number) => (
                          <span key={techIndex} className="tech-tag">{tech}</span>
                        ))}
                      </div>
                    )}
                    
                    {card.metadata.features && (
                      <div className="project-features">
                        <ul>
                          {card.metadata.features.map((feature: string, featureIndex: number) => (
                            <li key={featureIndex}>{feature}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    
                    {card.metadata.status && (
                      <div className="project-status">{card.metadata.status}</div>
                    )}
                    
                    {card.metadata.launchDate && (
                      <div className="project-launch-date">
                        <strong>Launched:</strong> {card.metadata.launchDate}
                      </div>
                    )}
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export { CardSectionNew, CardSectionLegacy };

